import { X } from '@phosphor-icons/react';
import { Button } from './Button';
import { SelectBox } from './SelectBox';
import { Tooltip } from './Tooltip';

export type Option = {
  value: string;
  label?: string;
};

export type TagMultiSelectProps = {
  values: string[];
  onChange: (values: string[]) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  tagClassName?: string;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  tagColor?: 'indigo' | 'viridian' | 'salmon' | 'gold' | 'neutral' | 'primary';
  maxVisibleTags?: number;
  selectBoxSize?: 'small' | 'medium' | 'large';
  selectBoxVariant?: 'outlined' | 'contained' | 'text';
};

const tagColorClasses = {
  indigo: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200',
  viridian: 'bg-viridian-100 text-viridian-800 hover:bg-viridian-200',
  salmon: 'bg-salmon-100 text-salmon-800 hover:bg-salmon-200',
  gold: 'bg-gold-100 text-gold-800 hover:bg-gold-200',
  neutral: 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200',
  primary: 'bg-primary-100 text-primary-800 hover:bg-primary-200',
};
export const TagMultiSelect = ({
  values,
  onChange,
  options,
  className = '',
  tagClassName = '',
  disabled = false,
  label,
  required = false,
  tagColor = 'primary',
  maxVisibleTags = 3,
  selectBoxSize = 'small',
  selectBoxVariant = 'outlined',
  placeholder = 'Select value...',
}: TagMultiSelectProps) => {
  const removeValue = (valueToRemove: string) => {
    onChange(values.filter((value) => value !== valueToRemove));
  };

  const addValue = (value: string) => {
    if (!values.includes(value)) {
      onChange([...values, value]);
    }
  };

  // Filter out already selected options
  const availableOptions = options.filter(
    (option) => !values.includes(option.value),
  );

  const RestTagsTooltip = () => {
    return (
      <div className="flex flex-col gap-2">
        {values.slice(maxVisibleTags).map((value) => (
          <div key={value}>{value}</div>
        ))}
      </div>
    );
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-neutral-700">
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}

      <div className="border border-neutral-200 rounded-lg p-3 min-h-[48px] bg-neutral-0">
        <div className="flex flex-wrap gap-2">
          {values.slice(0, maxVisibleTags).map((value) => (
            <div
              key={value}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${tagColorClasses[tagColor]} ${tagClassName}`}
            >
              <span>{value}</span>
              <Button
                variant="text"
                onClick={() => removeValue(value)}
                className="hover:bg-transparent rounded-full p-0.5"
                disabled={disabled}
                type="button"
                aria-label={`Remove ${value}`}
              >
                <X size={14} />
              </Button>
            </div>
          ))}
          {values.length > maxVisibleTags && (
            <Tooltip content={<RestTagsTooltip />}>
              <div className="flex items-center bg-neutral-100 text-neutral-600 px-3 py-1.5 rounded-full text-sm">
                +{values.length - maxVisibleTags}
              </div>
            </Tooltip>
          )}
          {availableOptions.length > 0 && (
            <SelectBox
              options={availableOptions}
              value={null}
              onChange={addValue}
              variant={selectBoxVariant}
              size={selectBoxSize}
              className="min-w-[120px]"
              disabled={disabled}
              placeholder={placeholder}
            />
          )}
        </div>
      </div>
    </div>
  );
};
